/**
 * Authentication API Hook
 * Integrates with the new API client for authentication operations
 */

'use client';

import { useState, useCallback } from 'react';
import { appApi } from '@/infrastructure/api';
import { ensureTokenValid } from '@/domains/auth/utils/token-checker';
import type {
  LoginRequest,
  RegisterRequest,
  LoginResponse,
  User,
  MFAVerifyRequest,
  ChangePasswordRequest
} from '@/infrastructure/api';

export interface AuthApiResult<T> {
  data?: T;
  error?: string;
  loading: boolean;
  success: boolean;
}

export interface AuthApiHook {
  // Authentication operations
  login: (credentials: LoginRequest) => Promise<AuthApiResult<LoginResponse>>;
  register: (userData: RegisterRequest) => Promise<AuthApiResult<User>>;
  logout: () => Promise<AuthApiResult<{ message?: string }>>;

  // MFA operations
  verifyMFA: (mfaData: MFAVerifyRequest) => Promise<AuthApiResult<LoginResponse>>;
  setupMFA: () => Promise<AuthApiResult<{ qr_code?: string; secret?: string; backup_codes?: string[] }>>;
  verifyMFASetup: (token: string) => Promise<AuthApiResult<{ success: boolean; access_token?: string; token_metadata?: any }>>;
  disableMFA: (token: string) => Promise<AuthApiResult<{ success: boolean }>>;

  // User profile operations
  getProfile: () => Promise<AuthApiResult<User>>;
  changePassword: (data: ChangePasswordRequest) => Promise<AuthApiResult<{ message?: string }>>;

  // Password recovery operations
  requestPasswordReset: (email: string) => Promise<AuthApiResult<{ message?: string }>>;
  confirmPasswordReset: (token: string, newPassword: string) => Promise<AuthApiResult<{ message?: string }>>;

  // Token management
  refreshToken: (refreshToken: string) => Promise<AuthApiResult<LoginResponse>>;

  // Health check
  getAuthHealth: () => Promise<AuthApiResult<{ status: string; timestamp: string }>>;

  // Loading states
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

export function useAuthApi(): AuthApiHook {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const executeRequest = useCallback(async <T>(
    requestFn: () => Promise<{ data: T; status: number; success: boolean }>,
    operation: string,
    requiresAuth: boolean = false
  ): Promise<AuthApiResult<T>> => {
    setIsLoading(true);
    setError(null);

    try {
      // Check token validity for authenticated requests
      if (requiresAuth) {
        console.log(`executeRequest: Checking token validity for ${operation}`);

        const isTokenValid = await ensureTokenValid(operation);
        if (!isTokenValid) {
          console.error(`executeRequest: Token validation failed for ${operation}`);
          setError('Your session has expired - please log in again');
          return {
            error: 'Your session has expired - please log in again',
            loading: false,
            success: false
          };
        }

        console.log(`executeRequest: Token is valid for ${operation}`);
      }

      console.log(`executeRequest: Starting ${operation}`);
      const response = await requestFn();
      console.log(`executeRequest: ${operation} response:`, response);

      if (response.success) {
        // For login operations, check if the actual login was successful
        if (operation === 'Login' && response.data && typeof response.data === 'object') {
          const loginData = response.data as any;
          if (loginData.success === false) {
            const errorMessage = loginData.message || `${operation} failed`;
            console.error(`executeRequest: ${operation} failed with response:`, response);
            setError(errorMessage);
            return {
              error: errorMessage,
              loading: false,
              success: false
            };
          }
        }

        return {
          data: response.data,
          loading: false,
          success: true
        };
      } else {
        const errorMessage = `${operation} failed`;
        console.error(`executeRequest: ${operation} failed with response:`, response);
        setError(errorMessage);
        return {
          error: errorMessage,
          loading: false,
          success: false
        };
      }
    } catch (err) {
      // Enhanced error logging for better debugging
      console.error(`executeRequest: ${operation} threw error:`, err);

      // Log error type and constructor
      console.error(`executeRequest: ${operation} error type:`, typeof err);
      console.error(`executeRequest: ${operation} error constructor:`, err?.constructor?.name);

      // Log error message and stack if it's an Error instance
      if (err instanceof Error) {
        console.error(`executeRequest: ${operation} error message:`, err.message);
        console.error(`executeRequest: ${operation} error stack:`, err.stack);
      }

      // Log error object keys and details if it's an object
      if (typeof err === 'object' && err !== null) {
        console.error(`executeRequest: ${operation} error keys:`, Object.keys(err));
        console.error(`executeRequest: ${operation} error details:`, JSON.stringify(err, null, 2));
      }

      // Log response details if available (for HTTP errors)
      if (err && typeof err === 'object' && 'response' in err) {
        const response = (err as any).response;
        console.error(`executeRequest: ${operation} response status:`, response?.status);
        console.error(`executeRequest: ${operation} response data:`, response?.data);
      }

      let errorMessage: string;

      if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'string') {
        errorMessage = err;
      } else if (err && typeof err === 'object' && 'message' in err) {
        errorMessage = (err as any).message;
      } else {
        errorMessage = `${operation} failed`;
      }

      setError(errorMessage);
      return {
        error: errorMessage,
        loading: false,
        success: false
      };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Authentication operations
  const login = useCallback(async (credentials: LoginRequest): Promise<AuthApiResult<LoginResponse>> => {
    return executeRequest(
      () => appApi.auth.login(credentials),
      'Login'
    );
  }, [executeRequest]);

  const register = useCallback(async (userData: RegisterRequest): Promise<AuthApiResult<User>> => {
    return executeRequest(
      () => appApi.auth.register(userData),
      'Registration'
    );
  }, [executeRequest]);

  const logout = useCallback(async (): Promise<AuthApiResult<{ message?: string }>> => {
    const result = await executeRequest(
      () => appApi.auth.logout(),
      'Logout'
    );

    // Clear tokens from API client after logout
    if (result.success) {
      appApi.clearAuthToken();
    }

    return result;
  }, [executeRequest]);

  // MFA operations
  const verifyMFA = useCallback(async (mfaData: MFAVerifyRequest): Promise<AuthApiResult<LoginResponse>> => {
    const result = await executeRequest(
      () => appApi.auth.verifyMFA(mfaData),
      'MFA Verification'
    );

    // Set token in API client if MFA verification succeeds
    if (result.success && result.data && result.data.access_token) {
      appApi.setAuthToken(result.data.access_token);
    }

    return result;
  }, [executeRequest]);

  const setupMFA = useCallback(async (): Promise<AuthApiResult<{ qr_code?: string; secret?: string; backup_codes?: string[] }>> => {
    console.log('useAuthApi: setupMFA called');
    console.log('appApi.auth:', appApi.auth);

    try {
      const result = await executeRequest(
        () => {
          console.log('About to call appApi.auth.setupMFA()');
          console.log('Base URL being used:', appApi.auth['config']?.baseUrl);
          return appApi.auth.setupMFA();
        },
        'MFA Setup',
        true // Requires authentication
      );
      console.log('useAuthApi: setupMFA result:', result);
      return result;
    } catch (error) {
      console.error('useAuthApi: setupMFA error:', error);
      return {
        error: error instanceof Error ? error.message : 'MFA Setup failed',
        loading: false,
        success: false
      };
    }
  }, [executeRequest]);

  /** LAUNCH GATE: MFA is IdP-only (Authentik). No app-side TOTP verification. */
  const verifyMFASetup = useCallback(async (_token: string): Promise<AuthApiResult<{ success: boolean; access_token?: string; token_metadata?: any }>> => {
    return {
      success: false,
      error: 'MFA is managed by your identity provider. Sign in at login.<domain> for MFA.',
      loading: false,
    };
  }, []);

  const disableMFA = useCallback(async (token: string): Promise<AuthApiResult<{ success: boolean }>> => {
    return executeRequest(
      () => appApi.auth.disableMFA(token),
      'MFA Disable',
      true // Requires authentication
    );
  }, [executeRequest]);

  // User profile operations
  const getProfile = useCallback(async (): Promise<AuthApiResult<User>> => {
    return executeRequest(
      () => appApi.auth.getProfile(),
      'Get Profile',
      true // Requires authentication
    );
  }, [executeRequest]);

  const changePassword = useCallback(async (data: ChangePasswordRequest): Promise<AuthApiResult<{ message?: string }>> => {
    return executeRequest(
      () => appApi.auth.changePassword(data),
      'Change Password',
      true // Requires authentication
    );
  }, [executeRequest]);

  // Password recovery operations
  const requestPasswordReset = useCallback(async (email: string): Promise<AuthApiResult<{ message?: string }>> => {
    return executeRequest(
      () => appApi.auth.requestPasswordReset(email),
      'Password Reset Request'
    );
  }, [executeRequest]);

  const confirmPasswordReset = useCallback(async (token: string, newPassword: string): Promise<AuthApiResult<{ message?: string }>> => {
    return executeRequest(
      () => appApi.auth.confirmPasswordReset(token, newPassword),
      'Password Reset Confirmation'
    );
  }, [executeRequest]);

  // Token management
  const refreshToken = useCallback(async (refreshTokenValue: string): Promise<AuthApiResult<LoginResponse>> => {
    const result = await executeRequest(
      () => appApi.auth.refreshToken(refreshTokenValue),
      'Token Refresh'
    );

    // Update token in API client if refresh succeeds
    if (result.success && result.data && result.data.access_token) {
      appApi.setAuthToken(result.data.access_token);
    }

    return result;
  }, [executeRequest]);

  // Health check
  const getAuthHealth = useCallback(async (): Promise<AuthApiResult<{ status: string; timestamp: string }>> => {
    return executeRequest(
      () => appApi.auth.getAuthHealth(),
      'Auth Health Check'
    );
  }, [executeRequest]);

  return {
    // Authentication operations
    login,
    register,
    logout,

    // MFA operations
    verifyMFA,
    setupMFA,
    verifyMFASetup,
    disableMFA,

    // User profile operations
    getProfile,
    changePassword,

    // Password recovery operations
    requestPasswordReset,
    confirmPasswordReset,

    // Token management
    refreshToken,

    // Health check
    getAuthHealth,

    // State
    isLoading,
    error,
    clearError
  };
}
